<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useMedicalCompanionStore} from "@/stores/Medical/MedicalCompanion";
import {computed, onMounted, ref} from "vue";
import axios from "axios";
import app from "@/App.vue";

export default {
    name: "MedicalCompanionForm",
    computed: {
        app() {
            return app
        }
    },
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {getBack, transformFilterValues, formatedDate, formatedTime} = useSharedMethod();
        const {$refs, $route, $i18n} = useTools();

        const medicalCompanionStore = useMedicalCompanionStore();

        const medicalCompanion = ref({
            appointments: [],
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/medicalCompanions/${id}`).then((response) => {
                medicalCompanion.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/medicalCompanions', medicalCompanion.value).then((response) => {
                    medicalCompanionStore.addMedicalCompanion(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/medicalCompanions', medicalCompanion.value).then((response) => {
                    medicalCompanionStore.updateMedicalCompanion(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/medicalCompanions/${medicalCompanion.value.id}`).then(() => {
                    medicalCompanionStore.deleteMedicalCompanion(medicalCompanion.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        const workerHeaders = [
            {
                text: $i18n.t('medical.to_plan'),
                align: "start",
                sortable: false,
                value: "action",
            },
            {
                text: $i18n.t("common.code"),
                align: "start",
                sortable: true,
                value: "code",
                fixed: true,
            },
            {
                text: $i18n.t("module.worker"),
                align: "start",
                sortable: true,
                value: "name",
                fixed: true,
            },
            {
                text: $i18n.t("module.occupation"),
                align: "start",
                sortable: true,
                value: "occupationName",
            },
            {
                text: $i18n.t("module.organizational_chart"),
                align: "start",
                sortable: true,
                value: "organizationalChartName",
            },
        ];
        const workers = ref([]);
        const loadingWorker = ref(false);

        function findWorkers() {
            loadingWorker.value = true;
            let filter = {};
            filter.allRecords = true;
            filter.sortBy = 'lastName';
            filter.phase = 'ACTIVE';
            axios.get('/workers/findAllForSelectPurpose', {
                params: transformFilterValues(filter),
            }).then(response => {
                workers.value = response.data;
            }).finally(() => {
                loadingWorker.value = false;
            });
        }

        const appointmentHeaders = [
            {
                text: "...",
                align: "start",
                sortable: false,
                value: "action",
                width: "0%"
            },
            {
                text: $i18n.t("common.code"),
                align: "start",
                sortable: true,
                value: "workerCode",
                width: "0%"
            },
            {
                text: $i18n.t("module.worker"),
                align: "start",
                sortable: true,
                value: "workerName",
                width: "0%"
            },
            {
                text: $i18n.t("common.date"),
                align: "start",
                sortable: true,
                value: "date",
                width: "0%"
            },
            {
                text: $i18n.t("common.time"),
                align: "start",
                sortable: true,
                value: "time",
                width: "0%"
            },
            {
                text: $i18n.t("common.state"),
                align: "start",
                sortable: true,
                value: "stateName",
                width: "0%"
            },
            {
                text: $i18n.t("common.result"),
                align: "start",
                sortable: true,
                value: "resultName",
                width: "0%"
            },
            {
                text: $i18n.t("common.comment"),
                align: "start",
                sortable: true,
                value: "observation",
            },
        ];
        const appointmentActions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    setAppointment(item);
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    let index = medicalCompanion.value.appointments.findIndex(e => e.workerId === item.workerId);
                    if(index > -1) {
                        medicalCompanion.value.appointments.splice(index, 1);
                    }
                },
            },
        ];
        function defaultAppointment() {
            return {
                workerId: null,
                workerCode: null,
                workerName: null,
            };
        }
        const appointment = ref(defaultAppointment());

        const planDialog = ref(false);

        function planWorker(worker) {
            appointment.value.workerId = worker.id;
            appointment.value.workerCode = worker.code;
            appointment.value.workerName = worker.name;
            
            planDialog.value = true;
        }

        function closePlanDialog() {
            planDialog.value = false;
            appointment.value = defaultAppointment();
        }

        function addPlan() {
            medicalCompanion.value.appointments.unshift({
                ...appointment.value,
            });
            closePlanDialog();
        }

        const appointmentDialog = ref(false);

        function setAppointment(item) {
            appointment.value = item;
            appointmentDialog.value = true;
        }

        function closeAppointmentDialog() {
            appointmentDialog.value = false;
            appointment.value = defaultAppointment();
        }

        onMounted(() => {
            findWorkers();
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            medicalCompanionStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            medicalCompanion,
            workers,
            workerHeaders,
            loadingWorker,
            planDialog,
            appointment,
            planWorker,
            closePlanDialog,
            addPlan,
            appointmentHeaders,
            appointmentActions,
            appointmentDialog,
            setAppointment,
            closeAppointmentDialog,
            save,
            update,
            remove,
            getBack,
            formatedDate,
            formatedTime
        };
    }
}
</script>

<template>
    <div>
        <v-dialog
            v-model="dialog"
            max-width="1200"
            persistent
            scrollable
        >
            <v-card>
                <w-dialog-toolbar
                    :view-action="viewAction"
                    :delete-action="deleteAction"
                    :add-action="addAction"
                    :update-action="updateAction"
                />
                <w-overlay
                    :overlay="overlay"
                    :loading="loading"
                    :delete-action="deleteAction"
                    @remove="remove"
                    @cancel="overlay = false"
                />
                <v-card-text>
                    <v-container grid-list-md>
                        <v-form ref="form">
                            <w-fieldset :title="$t('common.details')">
                                <v-row dense>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.code')" required/>
                                        <w-text-field
                                            v-model="medicalCompanion.code"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.label')" required/>
                                        <w-text-field
                                            v-model="medicalCompanion.name"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('module.medical_reason')" required/>
                                        <str-medical-reason-autocomplete
                                            v-model="medicalCompanion.reasonId"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('module.medical_center')" required/>
                                        <str-medical-center-autocomplete
                                            v-model="medicalCompanion.centerId"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.from')" required/>
                                        <w-date-picker
                                            v-model="medicalCompanion.start"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.to')" required/>
                                        <w-date-picker
                                            v-model="medicalCompanion.end"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('module.workers')">
                                <w-data-table
                                    :headers="workerHeaders"
                                    :items="workers"
                                    :loading="loadingWorker"
                                    :total="workers.length"
                                    disable-pagination
                                    fixed-header
                                    height="300"
                                >
                                    <template v-slot:[`item.action`]="{ item }">
                                        <w-btn-icon
                                            icon="mdi-calendar"
                                            color="purple"
                                            @press="planWorker(item)"
                                        />
                                    </template>
                                    <template v-slot:[`item.code`]="{ item }">
                                        <w-chip :text="item.code"/>
                                    </template>
                                    <template v-slot:[`item.name`]="{ item }">
                                        <w-inline-text bold :text="item.name"/>
                                    </template>
                                    <template v-slot:[`item.occupationName`]="{ item }">
                                        {{ item.occupationName }}
                                    </template>
                                    <template v-slot:[`item.organizationalChartName`]="{ item }">
                                        {{ item.organizationalChartName }}
                                    </template>
                                </w-data-table>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('module.medical_appointment')">
                                <w-data-table
                                    :headers="appointmentHeaders"
                                    :actions="appointmentActions"
                                    :items="medicalCompanion.appointments"
                                    :total="medicalCompanion.appointments.length"
                                    disable-pagination
                                    identity="workerId"
                                    fixed-header
                                    height="300"
                                >
                                    <template v-slot:[`item.workerCode`]="{ item }">
                                        <w-chip :text="item.workerCode"/>
                                    </template>
                                    <template v-slot:[`item.workerName`]="{ item }">
                                        <w-inline-text bold :text="item.workerName"/>
                                    </template>
                                    <template v-slot:[`item.date`]="{ item }">
                                        <w-inline-text :text="formatedDate(item.date)"/>
                                    </template>
                                    <template v-slot:[`item.time`]="{ item }">
                                        <w-inline-text :text="formatedTime(item.time)"/>
                                    </template>
                                </w-data-table>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('common.comment')">
                                <v-row dense>
                                    <v-col cols="12">
                                        <w-simple-text-area
                                            v-model="medicalCompanion.observation"
                                            :readonly="readonly"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        :show="addAction"
                        :loading="loading"
                        @press="save(false)"
                    />
                    <w-btn-update
                        :show="updateAction"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="planDialog"
            v-if="planDialog"
            persistent
            max-width="500"
            scrollable
        >
            <v-card>
                <w-toolbar
                    color="purple"
                    :title="appointment.workerName"
                />
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="planForm">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.date')" required/>
                                    <w-date-picker
                                        v-model="appointment.date"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.time')" required/>
                                    <w-time-picker
                                        v-model="appointment.time"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="closePlanDialog"/>
                    <w-btn
                        icon="mdi-calendar"
                        color="purple"
                        :text="$t('medical.to_plan')"
                        @press="addPlan"/>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="appointmentDialog"
            v-if="appointmentDialog"
            persistent
            max-width="500"
            scrollable
        >
            <v-card>
                <w-toolbar
                    color="orange"
                    :title="appointment.workerName"
                />
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="appointmentForm">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.date')" required/>
                                    <w-date-picker
                                        v-model="appointment.date"
                                        readonly
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.time')" required/>
                                    <w-time-picker
                                        v-model="appointment.time"
                                        readonly
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('data_select.MEDICAL_APPOINTMENT_STATE')" required/>
                                    <w-data-select-autocomplete
                                        v-model="appointment.stateId"
                                        type="MEDICAL_APPOINTMENT_STATE"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('data_select.MEDICAL_APPOINTMENT_RESULT')" required/>
                                    <w-data-select-autocomplete
                                        v-model="appointment.resultId"
                                        type="MEDICAL_APPOINTMENT_RESULT"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.comment')"/>
                                    <w-simple-text-area
                                        v-model="appointment.observation"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="closeAppointmentDialog"/>
                    <w-btn-update
                        show
                        color="orange"
                        @press="closeAppointmentDialog"/>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>